/**
 * عميل الـ API: نفس الأصل (Vite يمرّر /api في التطوير، وnginx في الإنتاج).
 * يعتمد على كوكي الجلسة (httpOnly) + رمز وصول في الذاكرة، ويجدّد تلقائياً عند 401.
 */

export interface ApiFieldError {
  path: string;
  message: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fields: ApiFieldError[];

  constructor(status: number, code: string, messageAr: string, fields: ApiFieldError[] = []) {
    super(messageAr);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.fields = fields;
  }
}

let accessToken: string | null = null;
let refreshPromise: Promise<boolean> | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

async function tryRefresh(): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = fetch('/api/auth/refresh', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    })
      .then(async (response) => {
        if (!response.ok) return false;
        const data = (await response.json()) as { accessToken?: string };
        if (data.accessToken) accessToken = data.accessToken;
        return true;
      })
      .catch(() => false)
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, unknown>;
  signal?: AbortSignal;
  raw?: boolean;
  retryOn401?: boolean;
}

function buildUrl(path: string, query?: Record<string, unknown>): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) value.forEach((v) => params.append(key, String(v)));
    else params.append(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, signal, raw = false, retryOn401 = true } = options;

  const headers: Record<string, string> = {};
  if (body !== undefined && !(body instanceof FormData)) headers['Content-Type'] = 'application/json';
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  const response = await fetch(buildUrl(path, query), {
    method,
    credentials: 'include',
    headers,
    signal,
    ...(body === undefined ? {} : { body: body instanceof FormData ? body : JSON.stringify(body) }),
  });

  if (response.status === 401 && retryOn401 && !path.includes('/auth/')) {
    const refreshed = await tryRefresh();
    if (refreshed) return request<T>(path, { ...options, retryOn401: false });
  }

  if (response.status === 204) return undefined as T;

  if (raw) {
    if (!response.ok) throw await toApiError(response);
    return response as unknown as T;
  }

  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    if (!response.ok) throw await toApiError(response);
    return (await response.text()) as unknown as T;
  }

  const payload = (await response.json()) as unknown;

  if (!response.ok) {
    const error = payload as { error?: { code?: string; messageAr?: string; fields?: ApiFieldError[] } };
    throw new ApiError(
      response.status,
      error?.error?.code ?? 'ERROR',
      error?.error?.messageAr ?? 'حدث خطأ غير متوقع',
      error?.error?.fields ?? [],
    );
  }

  return payload as T;
}

async function toApiError(response: Response): Promise<ApiError> {
  try {
    const payload = (await response.json()) as { error?: { code?: string; messageAr?: string; fields?: ApiFieldError[] } };
    return new ApiError(
      response.status,
      payload?.error?.code ?? 'ERROR',
      payload?.error?.messageAr ?? 'حدث خطأ غير متوقع',
      payload?.error?.fields ?? [],
    );
  } catch {
    return new ApiError(response.status, 'ERROR', `فشل الطلب (${response.status})`);
  }
}

export const apiGet = <T>(path: string, query?: Record<string, unknown>, signal?: AbortSignal): Promise<T> =>
  request<T>(path, { method: 'GET', query, signal });

export const apiPost = <T>(path: string, body?: unknown): Promise<T> =>
  request<T>(path, { method: 'POST', body });

export const apiPatch = <T>(path: string, body?: unknown): Promise<T> =>
  request<T>(path, { method: 'PATCH', body });

export const apiPut = <T>(path: string, body?: unknown): Promise<T> =>
  request<T>(path, { method: 'PUT', body });

export const apiDel = <T>(path: string): Promise<T> => request<T>(path, { method: 'DELETE' });

export const apiUpload = <T>(path: string, file: File): Promise<T> => {
  const form = new FormData();
  form.append('file', file);
  return request<T>(path, { method: 'POST', body: form });
};

/** تنزيل ملف من الخادم (CSV / PDF) مع احترام المصادقة */
export async function downloadFile(path: string, filename: string, query?: Record<string, unknown>): Promise<void> {
  const headers: Record<string, string> = {};
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  let response = await fetch(buildUrl(path, query), { credentials: 'include', headers });
  if (response.status === 401) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
      response = await fetch(buildUrl(path, query), { credentials: 'include', headers });
    }
  }
  if (!response.ok) throw await toApiError(response);

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

/** فتح مستند للطباعة في نافذة جديدة (يُستخدم لطباعة كشوف الرواتب والتقارير) */
export async function openPrintable(path: string, query?: Record<string, unknown>): Promise<void> {
  const headers: Record<string, string> = {};
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  const response = await fetch(buildUrl(path, query), { credentials: 'include', headers });
  if (!response.ok) throw await toApiError(response);
  const html = await response.text();
  const win = window.open('', '_blank');
  if (!win) throw new ApiError(0, 'POPUP_BLOCKED', 'يرجى السماح بالنوافذ المنبثقة للطباعة');
  win.document.write(html);
  win.document.close();
}

export { buildUrl };
